import React, { useEffect, useState } from "react";
import { Modal, Nav, Form } from "react-bootstrap";
import { Plus, Minus } from "lucide-react";

const API = process.env.API_URL || "http://localhost:4000/api";
const DASH = "\u2014";
const fp = (v) => (v == null ? DASH : v.toFixed(2) + "%");
const fn = (v) => (v == null ? DASH : Number(v).toLocaleString());
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const monthLabel = (d) => MON[+d.slice(5, 7) - 1] + " " + d.slice(0, 4);

function TradesModal({ ticker, show, onHide }) {
  const [tab, setTab] = useState("insider");
  const [d, setD] = useState(null);
  const [hide, setHide] = useState(false);
  useEffect(() => {
    if (!show) return;
    fetch(`${API}/stocks/${ticker}/share-trades`).then((r) => r.json()).then(setD).catch(() => setD({ insider: [], large: [] }));
  }, [show, ticker]);
  const rows = (d ? d.insider : []).filter((r) => !(hide && r.price && Number(r.shares) * Number(r.price) < 10000));
  const groups = {};
  rows.forEach((r) => { (groups[monthLabel(r.d)] = groups[monthLabel(r.d)] || []).push(r); });
  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton><Modal.Title>{ticker} trades</Modal.Title></Modal.Header>
      <Modal.Body>
        <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
          <Nav.Item><Nav.Link eventKey="insider">Insider Trades</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="large">Large holder filings (13D/13G)</Nav.Link></Nav.Item>
        </Nav>
        {!d && <div className="text-muted">Loading...</div>}
        {d && tab === "insider" && (
          <>
            <Form.Check type="checkbox" label="Hide small trades (under $10,000)" checked={hide} onChange={(e) => setHide(e.target.checked)} className="mb-2" />
            <p className="text-muted small">Open-market purchases and sales reported on SEC Form 4.</p>
            {!rows.length && <div className="text-muted">No open-market insider trades found.</div>}
            {Object.keys(groups).map((g) => (
              <div key={g} className="mb-3">
                <div className="sh-month">{g}</div>
                <table className="sh-table sh-trades">
                  <thead><tr><th>Person</th><th></th><th className="r">Quantity</th><th className="r">Avg price</th><th className="r">Value</th></tr></thead>
                  <tbody>
                    {groups[g].map((r, i) => (
                      <tr key={i}>
                        <td>{r.insider}<div className="small text-muted">{r.role || ""} {r.d}</div></td>
                        <td><span className={r.code === "P" ? "sh-buy" : "sh-sell"}>{r.code === "P" ? "Buy" : "Sell"}</span></td>
                        <td className="r">{fn(r.shares)}</td>
                        <td className="r">{r.price ? "$" + Number(r.price).toFixed(2) : DASH}</td>
                        <td className="r">{r.price ? "$" + Math.round(Number(r.shares) * Number(r.price)).toLocaleString() : DASH}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}
        {d && tab === "large" && (
          <>
            <p className="text-muted small">Schedule 13D/13G filings: investors crossing 5% ownership. The US equivalent of India's SAST disclosures.</p>
            {!d.large.length && <div className="text-muted">No 13D/13G filings stored for this company.</div>}
            <table className="sh-table"><tbody>
              {d.large.map((r, i) => (
                <tr key={i}><td>{r.filed}</td><td>{r.form}</td><td>{r.description || ""}</td><td className="r">{r.url ? <a href={r.url} target="_blank" rel="noreferrer">View</a> : ""}</td></tr>
              ))}
            </tbody></table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function ShareholdingPattern({ ticker }) {
  const [view, setView] = useState("quarterly");
  const [d, setD] = useState(null);
  const [open, setOpen] = useState({});
  const [modal, setModal] = useState(false);
  useEffect(() => {
    if (!ticker) return;
    let on = true;
    setD(null);
    fetch(`${API}/stocks/${ticker}/shareholding?view=${view}`).then((r) => r.json()).then((j) => on && setD(j)).catch(() => on && setD({ periods: [], rows: [], note: "Could not load shareholding." }));
    return () => { on = false; };
  }, [ticker, view]);

  return (
    <section className="sh-wrap">
      <div className="sh-head">
        <div><h3>Shareholding Pattern</h3><div className="text-muted small">Numbers in % of shares outstanding</div></div>
        <div className="sh-actions">
          <button className="sh-btn" onClick={() => setModal(true)}>TRADES</button>
          <div className="sh-toggle">
            <button className={view === "quarterly" ? "on" : ""} onClick={() => setView("quarterly")}>Quarterly</button>
            <button className={view === "yearly" ? "on" : ""} onClick={() => setView("yearly")}>Yearly</button>
          </div>
        </div>
      </div>
      {!d && <div className="text-muted">Loading...</div>}
      {d && !d.periods.length && <div className="text-muted">{d.note || "No data."}</div>}
      {d && d.periods.length > 0 && (
        <div className="sh-scroll">
          <table className="sh-table">
            <thead><tr><th></th>{d.periods.map((p) => <th key={p} className="r">{p}</th>)}</tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <React.Fragment key={r.key}>
                  <tr>
                    <td className="sh-label">
                      {r.children && r.children.length > 0 ? (
                        <button className="sh-x" onClick={() => setOpen({ ...open, [r.key]: !open[r.key] })}>{open[r.key] ? <Minus size={12} /> : <Plus size={12} />}</button>
                      ) : <span className="sh-x-sp" />}
                      {r.label}
                    </td>
                    {r.values.map((v, i) => <td key={i} className="r">{r.kind === "count" ? fn(v) : fp(v)}</td>)}
                  </tr>
                  {open[r.key] && r.children.map((c, j) => (
                    <tr key={r.key + j} className="sh-child">
                      <td className="sh-label sh-ind">{c.label}</td>
                      {c.values.map((v, i) => <td key={i} className="r">{fp(v)}</td>)}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d && d.note && d.periods.length > 0 && <p className="text-muted small mt-2">{d.note}</p>}
      <TradesModal ticker={ticker} show={modal} onHide={() => setModal(false)} />
    </section>
  );
}